<template>
  <Dialog
    :visible="display"
    :closable="false"
    :showHeader="false"
    :modal="true"
    class="verification-modal"
    :breakpoints="{
      '1024px': '60vw',
      '768px': '80vw',
      '575px': '94vw',
    }"
    :style="{ width: '30vw' }"
  >
    <div v-if="parseInt(counter, 10) >= 0">
      <div class="title mb-3 text-end">
        {{ headerMessage }}
      </div>
      <form class="mb-3 d-flex flex-wrap align-items-center justify-content-center">
        <input
          type="number"
          v-model="num1"
          class="digit"
          :min="0"
          :max="9"
          ref="num1"
          @input="onDigitInput($event, 1)"
          @focus="this.num1 = null"
          autofocus
        />
        <input
          type="number"
          v-model="num2"
          class="digit"
          :min="0"
          :max="9"
          ref="num2"
          @input="onDigitInput($event, 2)"
          @keyup.delete="!num2 ? this.$refs.num1.focus() : ''"
          @focus="this.num2 = null"
        />
        <input
          type="number"
          v-model="num3"
          class="digit"
          :min="0"
          :max="9"
          ref="num3"
          @input="onDigitInput($event, 3)"
          @keyup.delete="!num3 ? this.$refs.num2.focus() : ''"
          @focus="this.num3 = null"
        />
        <input
          type="number"
          v-model="num4"
          class="digit"
          :min="0"
          :max="9"
          ref="num4"
          @input="onDigitInput($event, 4)"
          @keyup.delete="!num4 ? this.$refs.num3.focus() : ''"
          @focus="this.num4 = null"
        />
        <input
          type="number"
          v-model="num5"
          class="digit"
          :min="0"
          :max="9"
          ref="num5"
          @input="onDigitInput($event, 5)"
          @keyup.delete="!num5 ? this.$refs.num4.focus() : ''"
          @focus="this.num5 = null"
        />
        <input
          type="number"
          v-model="num6"
          class="digit"
          :min="0"
          :max="9"
          ref="num6"
          @input="onDigitInput($event, 6)"
          @focus="this.num6 = null"
          @keyup.delete="!num6 ? this.$refs.num5.focus() : ''"
        />
      </form>
      <div class="send-again-link-cont d-flex justify-content-center">
        <div class="counter mx-4">{{ formattedCounter }}</div>
        <div
          class="send-again-link pb-4"
          @click="resendOTP"
          :class="{ 'disabled-link': !isUserAllowedToResend }"
        >
          إرسال رمز التحقق مرة أخرى؟
        </div>
      </div>
      <div class="d-flex align-items-center justify-content-around mt-3">
        <div class="w-50 px-3">
          <Button
            class="p-button-rounded px-5 w-100"
            :class="{'button-loading' : this.loading}"
            @click.stop="submit"
            :disabled="!isOTPFilled || this.loading"
            :label="this.loading && counter !== 0 ? '' : 'تحقق'"
            :icon="this.loading && counter !== 0 ? 'pi pi-spin pi-spinner' : ''"
          />
        </div>
        <div class="w-50 px-3">
          <Button
            class="p-button-rounded verification-btn w-100"
            @click.stop="close"
            label="إغلاق"
          />
        </div>
      </div>
    </div>
    <div v-else class="text-center color-primary">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
    </div>
  </Dialog>
</template>

<script>
import Dialog from "primevue/dialog";
import OTPService from "@/services/otp.service";
import WalletService from "@/services/wallet.service";
import { ToastSeverity } from "primevue/api";

export default {
  name: "verification-modal",
  components: { Dialog },
  props: [
    "display",
    "headerMessage",
    "mobileNumber",
    "email",
    "requestType",
    "model_id",
    "transferProcess",
    "selectedIds",
    "verificationError",
    "donationsLogAccess",
    "advanceDonationAccess",
  ],
  emits: ["onClose"],
  updated() {
    if (!this.display) return;
  },
  unmounted() {
    this.resetData();
  },
  data() {
    return {
      num1: null,
      num2: null,
      num3: null,
      num4: null,
      num5: null,
      num6: null,
      counterInterval: null,
      counter: null,
      numberOfAttemptsPerMinute: 3,
      loading: false,
    };
  },
  methods: {
    /**
     * One digit per box, but a paste (or a fast typist) delivers the whole code
     * to whichever box has focus: `type="number"` ignores maxlength, so "123456"
     * all landed in box 1 and `isOTPFilled` never became true.
     *
     * A single character keeps exactly the old behaviour - advance, or blur on
     * the last box - so normal typing and the charity transfer flow are
     * untouched.
     */
    onDigitInput(event, index) {
      const digits = String(event?.target?.value ?? "").replace(/\D/g, "");

      if (digits.length > 1) {
        this.spreadDigits(index, digits);
        return;
      }

      if (index === 6) this.$refs.num6.blur();
      else this.$refs["num" + (index + 1)].focus();
    },

    /** Fills the boxes from `startIndex` onwards, ignoring any overflow. */
    spreadDigits(startIndex, digits) {
      const boxes = ["num1", "num2", "num3", "num4", "num5", "num6"];
      let lastIndex = startIndex;

      for (let i = 0; i < digits.length && startIndex - 1 + i < boxes.length; i++) {
        this[boxes[startIndex - 1 + i]] = digits[i];
        lastIndex = startIndex + i;
      }

      // Every box clears itself on focus, so moving focus onto the last filled
      // one would wipe the digit just written. Blur instead: the code is
      // complete and the confirm button is now enabled.
      this.$nextTick(() => {
        const last = this.$refs["num" + Math.min(lastIndex, boxes.length)];
        if (last) last.blur();
      });
    },

    submit() {
      this.loading = true;
      if (this.numberOfAttemptsPerMinute <= 0) {
        this.$toast.add({
          severity: ToastSeverity.ERROR,
          detail:
            "لقد وصلت إلى الحد الأقصى لعدد المحاولات، يرجى المحاولة بعد انتهاء الفترة المحددة",
          life: 3000,
        });
        return;
      }

      this.numberOfAttemptsPerMinute--;
      if (this.transferProcess) {
        this.$emit("transfer", this.otp)
      } else if (this.advanceDonationAccess) {
        this.$emit("onClose", { isOtpVerified: true, otp: this.otp });
        this.loading = false;
        this.resetData();
      } else if (this.donationsLogAccess) {
        OTPService.verifyDonationsLogOTP(this.otp)
          .then(({ data }) => {
            if (data.status_code === 200) {
              this.$toast.add({
                severity: ToastSeverity.SUCCESS,
                detail: data.result,
                life: 3000,
              });
              this.$emit("onClose", { isOtpVerified: true });
            }
          })
          .catch(() => {
            this.close();
          });
      } else {
        OTPService.verifyOTP({
          otp: this.otp,
          request_type: this.requestType,
          mobile_number: this.mobileNumber || "",
          email: this.email || "",
          model_id: this.model_id || 0,
        })
            .then(({ data: { result } }) => {
              this.$toast.add({
                severity: ToastSeverity.SUCCESS,
               detail: "تم التحقق بنجاح",
                life: 3000,
              });
              this.$emit("onClose", { isOtpVerified: true });
              this.loading = false;
              this.resetData();
            })
            .catch(({ response }) => {
              this.loading = false;
              if (response.data) {
                const { errors } = response.data;
                if (
                    errors &&
                    errors.length &&
                    errors.includes("انتهت صلاحية الرمز")
                ) {
                  this.resendOTP();
                }
              }
            });
      }
    },
    resendOTP() {
      this.loading = false;
      this.resetData();
      if (this.advanceDonationAccess) {
        WalletService.sendAdvanceDonationOtp()
          .then(({ data }) => {
            if (data.status_code === 200) {
              this.counter = 120;
              this.startCounter();
              setTimeout(() => { this.$refs.num1.focus(); });
            }
          })
          .catch(() => { this.close(); });
      } else if (this.transferProcess) {
        const id = this.selectedIds ? this.selectedIds : [this.$route.params.id];
        OTPService.paymentSendOtp(id)
            .then(({ data: { result, status, status_code } }) => {
              if (status === 200 || status_code === 201) {
                this.$toast.add({
                  severity: ToastSeverity.SUCCESS,
                  detail: result[0].message,
                  life: 3000,
                });
                this.counter = 300;
              } else if (status === 202) this.counter = result;
              this.startCounter();
              setTimeout(() => {
                this.$refs.num1.focus();
              });
            })
            .catch(e => {
              this.close()
            })
      } else if (this.donationsLogAccess) {
        this.isLoading = true;
        OTPService.createDonationsLogOTP()
          .then(({ data }) => {
            if (data.status_code === 201) {
              this.$toast.add({
                severity: ToastSeverity.SUCCESS,
                detail: data.result[0].message,
                life: 3000,
              });
              this.counter = 300;
              this.startCounter();
              setTimeout(() => {
                this.$refs.num1.focus();
              });
            }
          })
          .catch(() => {
            this.close();
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        OTPService.resendOTP({
          request_type: this.requestType,
          mobile_number: this.mobileNumber || "",
          email: this.email || "",
          model_id: this.model_id || 0,
        }).then(({ data: { result, status } }) => {
          if (status === 200) {
            this.$toast.add({
              severity: ToastSeverity.SUCCESS,
              detail: "تم التحقق بنجاح",
              life: 3000,
            });
            this.counter = 300;
          } else if (status === 202) this.counter = result;
          this.startCounter();
          setTimeout(() => {
            this.$refs.num1.focus();
          });
        })
        .catch(() => {
          this.close();
        });
      }
    },
    startCounter() {
      this.targetTime = Date.now() + this.counter * 1000;
      this.counterInterval = setInterval(() => {
        const remainingTime = Math.max(0, this.targetTime - Date.now());
        this.counter = Math.floor(remainingTime / 1000);
        if (this.counter === 0) {
          clearInterval(this.counterInterval);
        }
      }, 100);
    },
    close() {
      this.resetData();
      this.$emit("onClose", { isOtpVerified: false });
    },
    resetData() {
      this.num1 = null;
      this.num2 = null;
      this.num3 = null;
      this.num4 = null;
      this.num5 = null;
      this.num6 = null;

      this.numberOfAttemptsPerMinute = 3;
      this.counter = null;
      clearInterval(this.counterInterval);
    },
  },
  computed: {
    otp() {
      return (
        "" +
        this.num1 +
        this.num2 +
        this.num3 +
        this.num4 +
        this.num5 +
        this.num6
      );
    },
    isOTPFilled() {
      return (
        this.num1 != null &&
        this.num2 != null &&
        this.num3 != null &&
        this.num4 != null &&
        this.num5 != null &&
        this.num6 != null
      );
    },
    isUserAllowedToResend() {
      return this.counter === 0;
    },
    formattedCounter() {
      if (this.counter >= 60) {
        let minutes = Math.floor(this.counter / 60);
        let seconds = this.counter % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
      } else {
        return `0:${this.counter < 10 ? '0' : ''}${this.counter}`;
      }
    },
  },
  watch: {
    display(val) {
      if (val) {
        this.resendOTP();
      }
    },
    verificationError(val) {
      if (val) {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.title {
  color: #3f3f3f;
  font-size: 20px;
}

:deep(.p-dialog-content) {
  padding: 1.5rem 3rem !important;
}

:deep(.digit) {
  padding: 0;
  margin: 0.4rem;
  height: clamp(36px, 9vw, 50px);
  width: clamp(36px, 9vw, 50px);
  font-size: clamp(0.9rem, 3.5vw, 1.1rem);
  border-radius: 5px;
  border-color: #d9d9d9;
  border-width: 1px;
  text-align: center;
}

@media (max-width: 575px) {
  :deep(.p-dialog-content) {
    padding: 1.25rem 1rem !important;
  }

  :deep(.digit) {
    margin: 0.25rem;
  }
}

.send-again-link-cont {
  .send-again-link {
    cursor: pointer;
    text-align: center;
    color: #12b2aa;
    font-size: 12px;
    text-decoration: underline;

    &.disabled-link {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  border-bottom: 1px solid #ececec;
}
.button-loading {
  border-radius: 2rem !important;
}
.verification-btn {
  background-color: #d9d9d9;
  border-color: #d9d9d9;
  border-radius: 25px;
  color: #3f3f3f;
}

.verification-btn:disabled {
  background-color: #ececec;
  border-color: #ececec;
}

.verification-btn:hover {
  background-color: #b2b2b2;
  border-color: #b2b2b2;
  color: #3f3f3f;
}

input {
    &[type="number"] {
        -moz-appearance: textfield;
    }
    &[type="number"]::-webkit-inner-spin-button,
    &[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    
}
</style>
